import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import cN from 'classnames';
import { Calendar, ICalendarProps, IDetailDateData } from '@lion-design/core';
import { GroupAllGuaranteed } from '@components/DetailPage';
import {
    setGroupDetail,
    setGroupAllCalendar,
    setCalendarGroupsMonth,
    setStationInfo,
    setPricesInfo,
    setFlightInfo,
} from '@Redux/detail/Group/actions';
import {
    // setOptionalInfo,
    setNoticeinfo,
} from '@Redux/detail/Itineraries/actions';
import * as Interface from '@Redux/detail/Group/interfaces';
import { RootState } from '@Redux/detail/store';
import { ArrowLeft } from '@components/Common/Icons';
import Spinner from '@components/Common/Spinner';
import { changeDetailHistory } from '@utils/utils';
import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';

dayjs.extend(isSameOrAfter);

interface I_Price {
    price: string;
}

interface I_GroupLabel {
    Guaranteed: boolean;
}

interface I_GroupState {
    Status: string;
    multiTrip: boolean;
}

interface I_DetailInfo {
    item: Interface.I_CalendarGroup;
    multiTrip: boolean;
}

function GroupAllCalendar() {
    const { CalendarGroups, GroupInfo, Schedules, currentTourID } = useSelector(
        (store: RootState) => store.groupReducer
    );
    const dispatch = useDispatch();
    const { isLoadingCalendar } = CalendarGroups;

    /*
        detailDateData 範例：
        {
            '2021-03-26': { note: <月曆內文 /> },
            '2021-03-27': { note: <月曆內文 /> },
            '2021-03-28': { note: <月曆內文 /> },
            ...
        }
    */
    const detailDateData: () => IDetailDateData | undefined = () => {
        if (CalendarGroups?.groups?.length > 0) {
            const detailDateData: IDetailDateData = {};
            const guaranteedData = CalendarGroups.groups?.filter((group) => {
                return group.Guaranteed == true;
            });
            const renderData =
                !CalendarGroups.isGuaranteedDisabled &&
                CalendarGroups.parmIsEnsureGroup &&
                CalendarGroups.isGuaranteedChecked
                    ? guaranteedData
                    : CalendarGroups?.groups;

            renderData.forEach((item: Interface.I_CalendarGroup) => {
                // 將 2021/03/26 轉換為 2021-03-26
                const reFormatDate = item.Date.replace(/\//g, '-');

                const multiTrip = Object.prototype.hasOwnProperty.call(
                    detailDateData,
                    reFormatDate
                );

                detailDateData[reFormatDate] = {
                    note: (
                        <DetailInfo
                            item={
                                multiTrip
                                    ? {
                                          ...detailDateData[reFormatDate].note.props.item,
                                      }
                                    : item
                            }
                            multiTrip={multiTrip}
                        />
                    ),
                };
            });

            return detailDateData;
        }
    };

    // 以行程 API (tourinfojson) 中得知最先&最後出發日期，得知最小&最大月份
    const isMaxMonth = (function compareScheduleMax() {
        const MaxMonth = dayjs(Schedules?.AllMaxGoDate).format('YYYY-MM');
        return dayjs(CalendarGroups.month).isSameOrAfter(MaxMonth);
    })();

    const isMinMonth = (function compareScheduleMin() {
        const MinMonth = dayjs(Schedules?.AllMinGoDate).format('YYYY-MM');
        return dayjs(CalendarGroups.month).isSameOrBefore(MinMonth);
    })();

    const MaxMonthDate = dayjs(Schedules && Schedules.AllMaxGoDate).format('YYYY-MM-DD');
    const MinMonthDate = dayjs(Schedules && Schedules.AllMinGoDate).format('YYYY-MM-DD');

    const getMonthStartAndEnd = (day: string) => {
        const start = dayjs(day).startOf('month').format('YYYY-MM-DD');
        const end = dayjs(day).endOf('month').format('YYYY-MM-DD');

        return [start, end];
    };

    const clickPrevOrNext = (condition: 'prev' | 'next') => {
        const month =
            condition === 'prev'
                ? dayjs(CalendarGroups.month).subtract(1, 'month').format('YYYY-MM')
                : dayjs(CalendarGroups.month).add(1, 'month').format('YYYY-MM');
        const [GoDateStart, GoDateEnd] = getMonthStartAndEnd(month);

        const reqBody = {
            NormGroupID: GroupInfo.NormGroupID,
            GoDateStart,
            GoDateEnd,
            TourID: currentTourID,
        };

        dispatch(setCalendarGroupsMonth(month));
        dispatch(setGroupAllCalendar(reqBody));
    };

    return (
        <div
            className={cN(styles['group_all_calendar'], 'group_all_Calendar', {
                [`${styles.show}`]: !CalendarGroups.isListMode,
                [`${styles.noData}`]: CalendarGroups.groups?.length < 1 && !isLoadingCalendar,
            })}
        >
            <GroupAllGuaranteed />
            <Calendar
                pickDate={GroupInfo.GoDate}
                detailDateData={detailDateData()}
                arrowPrevEle={<ArrowLeft fill={isMinMonth ? '#bbb' : '#669'} />}
                arrowNextEle={<ArrowLeft fill={isMaxMonth ? '#bbb' : '#669'} />}
                onClickPrevMonth={isMinMonth ? undefined : () => clickPrevOrNext('prev')}
                onClickNextMonth={isMaxMonth ? undefined : () => clickPrevOrNext('next')}
                isChineseMonth={false}
                curMonth={dayjs(CalendarGroups.month)}
                minDate={MinMonthDate}
                maxDate={MaxMonthDate}
            />
            {isLoadingCalendar && <Spinner />}
        </div>
    );
}

export default GroupAllCalendar;
export type I_GroupAllCalendar = ICalendarProps;

/**********************/

function Prices({ price }: I_Price) {
    return <div className={styles['price']}>{price}</div>;
}

function GroupLable({ Guaranteed }: I_GroupLabel) {
    const { t } = useTranslation();

    return Guaranteed ? <div className={styles['groupLable']}>{t('IsEnsureGroup')}</div> : null;
}

function GroupState({ Status, multiTrip }: I_GroupState) {
    return multiTrip ? (
        <div className={styles['multiTrip']}>今日多行程</div>
    ) : (
        <div className={styles['status']}>{Status}</div>
    );
}

function clickDetailDate(NormGroupID: string, ID: string, Date: string, dispatch: any) {
    const reqBody = {
        GroupID: ID,
    };

    const FlightInfoReqBody = {
        GroupID: ID,
        NormGroupID,
        GoDate: dayjs(Date).format('YYYY-MM-DD'),
    };

    dispatch(setGroupDetail(reqBody));
    dispatch(setStationInfo(reqBody));
    // dispatch(setItineraries(reqBody));
    // dispatch(setOptionalInfo(reqBody));
    dispatch(setNoticeinfo(reqBody));
    dispatch(setPricesInfo(reqBody));
    dispatch(setFlightInfo(FlightInfoReqBody));

    changeDetailHistory({
        NormGroupID,
        GroupID: ID,
    });
}

function DetailInfo({ item, multiTrip }: I_DetailInfo) {
    const { Price, ID, Date } = item;
    const { GoDate, NormGroupID } = useSelector((state: RootState) => state.groupReducer.GroupInfo);

    const dispatch = useDispatch();

    const isSameDay = dayjs(Date).isSame(GoDate);

    return (
        <div
            className={cN(styles['detailInfo'], { [styles.active]: isSameDay })}
            onClick={() => clickDetailDate(NormGroupID, ID, Date, dispatch)}
        >
            <GroupLable Guaranteed={item.Guaranteed} />
            <div className={styles['contentBlock']}>
                <Prices price={Price} />
                <GroupState Status={item.Status} multiTrip={multiTrip} />
                {item.IsVip && <div className={styles['vip_wrapper']}>會員限購</div>}
            </div>
        </div>
    );
}
